// AuditoriaView — leitura de audit_logs da company

import { signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { AlertCircle, Loader2, ScrollText, Search } from "lucide-react";
import { api, ApiError, type AuditLog } from "../api";
import { friendlyError } from "@app/ui/lib/friendly-error";

const logs = signal<AuditLog[] | null>(null);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listAudit({ q: search.value });
    logs.value = r.logs;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function AuditoriaView() {
  useEffect(() => {
    load();
  }, []);
  if (phase.value === "loading")
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  if (phase.value === "error")
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" /> {errMsg.value}
      </div>
    );
  const rows = logs.value ?? [];
  return (
    <div class="space-y-4">
      <header>
        <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
          <ScrollText className="size-6 text-primary" />
          Auditoria
        </h1>
        <p class="mt-1 text-sm text-muted-foreground">Logs de ações nesta organização.</p>
      </header>
      <div class="flex items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Buscar ação, entityType, ator…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          onClick={() => load()}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          Buscar
        </button>
      </div>
      {rows.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Sem registros.
        </div>
      ) : (
        <div class="overflow-hidden rounded-md border border-border bg-card/40">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                <th class="px-3 py-2">Quando</th>
                <th class="px-3 py-2">Ator</th>
                <th class="px-3 py-2">Ação</th>
                <th class="px-3 py-2">Entidade</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} class="border-b border-border/60 last:border-0 hover:bg-accent/30">
                  <td class="px-3 py-2 font-mono text-[11px] text-muted-foreground">
                    {new Date(l.at).toLocaleString("pt-BR")}
                  </td>
                  <td class="px-3 py-2 text-xs">{l.actorEmail ?? "—"}</td>
                  <td class="px-3 py-2 text-xs">
                    <span class="rounded bg-accent px-1.5 py-0.5 font-mono text-[10px]">{l.action}</span>
                  </td>
                  <td class="px-3 py-2 text-xs font-mono text-muted-foreground">
                    {l.entityType}
                    {l.entityId && ` · ${l.entityId.slice(0, 8)}…`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
